import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { organizationsAccessListOptions, type AuditLogList } from 'src/api/main'
import type { FacetOption } from 'src/components/FilterSelect'
import { toDisplayString } from 'src/utils'

import { OPTIONS_STALE_TIME_MS } from './useAuditFacetFilter'
import { useDebouncedSearch } from './useDebouncedSearch'

const ACTOR_OPTIONS_LIMIT = 20

const getActorLabel = (actor: AuditLogList['actor']) => {
  const id = toDisplayString(actor?.id)

  return actor
    ? [
        toDisplayString(actor.full_name),
        toDisplayString(actor.email),
        id ? `#${id}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : ''
}

/**
 * Actor filter options, server-searched against the organization's user
 * directory (the audit `actor_id` equals the member's user id). Keeps the
 * selected actor labelled even when it isn't in the current search page by
 * falling back to an audit row we already have, then to a remembered label, so
 * the raw `#id` never reappears once the actor has been resolved once.
 */
export const useActorFilter = (
  organizationId: number,
  selected: string,
  rows: AuditLogList[]
): {
  options: FacetOption[]
  onSearch: (text: string) => void
  loading: boolean
} => {
  const [search, onSearch] = useDebouncedSearch(300)

  const { data, isFetching } = useQuery({
    ...organizationsAccessListOptions({
      query: {
        organization: organizationId,
        per_page: ACTOR_OPTIONS_LIMIT,
        search: search || undefined
      }
    }),
    enabled: organizationId > 0,
    placeholderData: (previousData) => previousData,
    // Same policy as the facet dropdowns: a newly invited member shows up in the
    // actor filter on the next page entry after the list goes stale.
    refetchOnMount: true,
    staleTime: OPTIONS_STALE_TIME_MS
  })

  const options = useMemo<FacetOption[]>(
    () =>
      (data?.results ?? []).map((row) => ({
        value: String(row.user.id),
        label: getActorLabel({
          id: String(row.user.id),
          email: row.user.email,
          full_name:
            [row.user.first_name, row.user.last_name]
              .filter(Boolean)
              .join(' ') || null
        })
      })),
    [data?.results]
  )

  // Resolve the selected actor's label from whatever is live right now: the
  // member-directory search page, else an audit row that carries this actor.
  const liveSelected = useMemo<FacetOption | null>(() => {
    if (!selected) return null

    const fromList = options.find((option) => option.value === selected)
    if (fromList) return fromList

    const actor = rows.find(
      (row) => toDisplayString(row.actor?.id) === selected
    )?.actor

    return actor ? { value: selected, label: getActorLabel(actor) } : null
  }, [selected, options, rows])

  // Remember the resolved label so the actor stays named even when it drops out
  // of both sources — e.g. after another filter narrows the results to zero, the
  // raw `#id` would otherwise reappear for an actor the user clearly just picked.
  const [remembered, setRemembered] = useState<FacetOption | null>(null)
  if (
    liveSelected &&
    (!remembered ||
      remembered.value !== liveSelected.value ||
      remembered.label !== liveSelected.label)
  ) {
    setRemembered(liveSelected)
  }

  const selectedOption = useMemo<FacetOption | null>(() => {
    if (!selected) return null
    if (liveSelected) return liveSelected
    if (remembered?.value === selected) return remembered
    return { value: selected, label: `#${selected}` }
  }, [selected, liveSelected, remembered])

  const selectOptions = useMemo<FacetOption[]>(
    () =>
      selectedOption
        ? [
            selectedOption,
            ...options.filter((option) => option.value !== selectedOption.value)
          ]
        : options,
    [selectedOption, options]
  )

  return { options: selectOptions, onSearch, loading: isFetching }
}
